import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Target, TrendingUp, Brain, ArrowRight, Sparkles, CheckCircle2, XCircle, Activity, Trophy } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import Seo from '@/components/shared/Seo';
import { ieltsApi, goToIeltsAuth } from '@/lib/ieltsApi';

const SECTION_LABEL = { listening: 'Listening', reading: 'Reading', writing: 'Writing', speaking: 'Speaking' };
const SECTION_COLOR = { listening: '#dc2626', reading: '#b91c1c', writing: '#991b1b', speaking: '#7f1d1d' };

const KpiCard = ({ label, value, sub, icon: Icon, accent = 'black', testId }) => (
  <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
          <div className={`text-3xl sm:text-4xl font-bold mt-1.5 ${accent === 'red' ? 'text-red-700' : 'text-black'}`}>{value}</div>
          {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent === 'red' ? 'bg-red-50' : 'bg-gray-100'}`}>
          <Icon className={`h-5 w-5 ${accent === 'red' ? 'text-red-700' : 'text-gray-700'}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const IeltsDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsLogin, setNeedsLogin] = useState(false);
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    ieltsApi.dashboard()
      .then(setData)
      .catch((e) => { if (e.status === 401) setNeedsLogin(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-7 w-7 animate-spin text-red-700" /></div>;
  }
  if (needsLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-5 w-5 text-[#D3D86F]" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Sign in to view your dashboard</h1>
            <p className="text-sm text-gray-600 mb-6">Track your band, accuracy, and weak skills.</p>
            <Button onClick={() => goToIeltsAuth('/ielts/dashboard')}
              className="w-full bg-black hover:bg-gray-900 text-white rounded-full h-11"
              data-testid="ielts-dashboard-login">
              Sign in with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  const stats = data.stats;
  const sec = (k) => stats[k] || { total: 0, correct: 0, accuracy: 0 };
  const overall = stats.overall || { total: 0, correct: 0, accuracy: 0 };
  const firstName = data.user?.name?.split(' ')[0] || 'Learner';

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="ielts-dashboard">
      <Seo title="IELTS Dashboard · Winhouse" canonical="/ielts/dashboard" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-10 w-full">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-1.5">Dashboard</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Welcome back, {firstName}.</h1>
            <p className="text-gray-600 text-sm mt-1">Here's how your IELTS prep is going.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => navigate('/ielts/practice')} className="bg-red-700 hover:bg-red-800 text-white rounded-full h-11 px-6">
              Practice <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button onClick={() => navigate('/ielts/writing')} className="bg-black text-white rounded-full h-11 px-6 hover:bg-gray-900"
              data-testid="ielts-dash-writing-cta">
              Writing AI <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button onClick={() => navigate('/ielts/speaking')} className="bg-black text-white rounded-full h-11 px-6 hover:bg-gray-900"
              data-testid="ielts-dash-speaking-cta">
              Speaking AI <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button onClick={() => navigate('/ielts/mock-test')} variant="outline"
              className="rounded-full h-11 px-6 border-black hover:bg-black hover:text-white">
              Take Mock Test
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <KpiCard label="Band estimate · 0–9" value={data.estimated_band}
            sub={data.band_source === 'last_mock' ? 'From your last mock test' : 'From practice attempts'}
            icon={Trophy} accent="red" testId="ielts-dash-band" />
          <KpiCard label="Overall accuracy" value={`${overall.accuracy}%`}
            sub={`${overall.correct} / ${overall.total} correct (auto-graded)`}
            icon={Activity} />
          <KpiCard label="Questions attempted" value={overall.total}
            sub={overall.total ? 'Keep the streak going' : 'Start with a few questions'}
            icon={Target} />
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3 text-sm bg-gradient-to-br from-[#D3D86F]/15 to-amber-50/40 border border-[#D3D86F]/40 rounded-2xl px-5 py-4"
          data-testid="ielts-dash-ai-banner">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full bg-black text-[#D3D86F]">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
          </span>
          <div className="flex-1 min-w-[200px]">
            <strong className="font-semibold text-gray-900">Winhouse AI Feedback is active</strong>
            <span className="text-gray-700"> — submit a Writing or Speaking response and get an instant Claude-powered band score.</span>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/ielts/writing/practice')} size="sm"
              className="bg-black text-white rounded-full h-9 px-4 text-xs" data-testid="ielts-dash-ai-writing-cta">
              Submit Writing →
            </Button>
            <Button onClick={() => navigate('/ielts/speaking/practice')} size="sm"
              className="bg-red-700 hover:bg-red-800 text-white rounded-full h-9 px-4 text-xs" data-testid="ielts-dash-ai-speaking-cta">
              Submit Speaking →
            </Button>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          <span className="font-semibold">Estimate</span>
          <span className="text-amber-800">— Reading & Listening bands come from auto-graded questions; Writing & Speaking bands now come from Winhouse AI (Claude Sonnet 4.5).</span>
        </div>

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-4">Performance by section</div>
            <div className="space-y-5">
              {['listening','reading','writing','speaking'].map((k) => {
                const s = sec(k);
                return (
                  <div key={k} data-testid={`ielts-dash-sec-${k}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="w-2 h-2 rounded-full" style={{ background: SECTION_COLOR[k] }} />
                        <span className="font-semibold text-sm">{SECTION_LABEL[k]}</span>
                        <span className="text-xs text-gray-500">{s.correct} / {s.total} correct</span>
                      </div>
                      <span className="font-bold text-sm">{s.accuracy}%</span>
                    </div>
                    <Progress value={s.accuracy || 0} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Target className="h-4 w-4 text-red-600" />
                <span className="font-semibold">Weak skills</span>
                <Badge className="ml-auto bg-red-50 text-red-700 border border-red-100">Below 60%</Badge>
              </div>
              {data.weak_topics?.length ? (
                <ul className="space-y-3">
                  {data.weak_topics.map((w) => (
                    <li key={w.topic + w.section} className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="capitalize truncate">
                          {(w.topic || '').replace(/_/g,' ')} <span className="text-gray-400 text-xs ml-2">{SECTION_LABEL[w.section] || w.section}</span>
                        </span>
                        <span className="text-red-700 font-semibold">{w.accuracy}%</span>
                      </div>
                      <Progress value={w.accuracy} className="h-1.5" />
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-gray-500">No weak skills yet — keep practising.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Brain className="h-4 w-4 text-red-700" />
                <span className="font-semibold">Recommended next</span>
              </div>
              {data.recommended_next ? (
                <>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="text-xs uppercase tracking-wide text-red-700">{data.recommended_next.reason}</div>
                    <div className="font-bold text-lg capitalize mt-0.5">
                      {(data.recommended_next.topic || SECTION_LABEL[data.recommended_next.section] || '').replace(/_/g,' ')}
                    </div>
                  </div>
                  <Button onClick={() => navigate(`/ielts/practice?section=${data.recommended_next.section}${data.recommended_next.topic ? `&topic=${data.recommended_next.topic}` : ''}`)}
                    className="w-full bg-red-700 hover:bg-red-800 text-white rounded-full">
                    Practice this <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </>
              ) : <p className="text-sm text-gray-500">Take a few practice questions to get a recommendation.</p>}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4" />
              <span className="font-semibold">Recent practice</span>
            </div>
            {data.recent_practice?.length ? (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                      <th className="py-2.5 px-2">Section</th>
                      <th className="px-2">Type</th>
                      <th className="px-2">Topic</th>
                      <th className="px-2 text-right">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_practice.map((r, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-2.5 px-2">{SECTION_LABEL[r.section]}</td>
                        <td className="px-2 capitalize text-gray-600 text-xs">{(r.question_type || '').replace(/_/g,' ')}</td>
                        <td className="px-2 capitalize">{(r.topic || '').replace(/_/g,' ')}</td>
                        <td className="px-2 text-right">
                          {r.is_correct === true && <span className="inline-flex items-center gap-1 text-green-700 font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Correct</span>}
                          {r.is_correct === false && <span className="inline-flex items-center gap-1 text-red-700 font-medium"><XCircle className="h-3.5 w-3.5" /> Wrong</span>}
                          {r.is_correct === null && <span className="text-gray-500 text-xs">Manual review</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-gray-500">No practice yet. Tap Practice above to start.</p>}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};
export default IeltsDashboardPage;
